import { useState } from "react";
import "./styles.scss";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRangePickerProps {
    start: Date | null;
    end: Date | null;
    onChange: (range: DateRange) => void;
    minDate?: Date;
    maxDate?: Date;
}

const MONTH_NAMES = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];
const DAY_LABELS = ["S","M","T","W","T","F","S"];

function sod(d: Date) { // start of day
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function sameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function DateRangePicker({ start, end, onChange, minDate, maxDate }: DateRangePickerProps) {
    const today = sod(new Date());
    const [viewMonth, setViewMonth] = useState(() => (start ?? today).getMonth());
    const [viewYear, setViewYear]   = useState(() => (start ?? today).getFullYear());
    const [hovered, setHovered]     = useState<Date | null>(null);
    const [phase, setPhase]         = useState<"start" | "end">(start && !end ? "end" : "start");

    const rightMonth = (viewMonth + 1) % 12;
    const rightYear  = viewMonth === 11 ? viewYear + 1 : viewYear;

    function prev() {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    }
    function next() {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    }

    function handleClick(date: Date) {
        if (phase === "start") {
            onChange({ start: date, end: null });
            setPhase("end");
        } else {
            if (start && date < start) {
                onChange({ start: date, end: start });
            } else {
                onChange({ start, end: date });
            }
            setPhase("start");
        }
    }

    function inRange(date: Date) {
        const s = start ? sod(start) : null;
        const e = phase === "end" && hovered ? sod(hovered) : end ? sod(end) : null;
        if (!s || !e) return false;
        const lo = s < e ? s : e;
        const hi = s < e ? e : s;
        return date > lo && date < hi;
    }

    function disabled(date: Date) {
        if (minDate && date < sod(minDate)) return true;
        if (maxDate && date > sod(maxDate)) return true;
        return false;
    }

    // Is this date the range start cap?
    function isRangeStart(date: Date) {
        if (!start) return false;
        return sameDay(date, sod(start));
    }

    // Is this date the range end cap (real or hovered)?
    function isRangeEnd(date: Date) {
        const e = phase === "end" && hovered ? sod(hovered) : end ? sod(end) : null;
        if (!e) return false;
        return sameDay(date, e);
    }

    function renderMonth(year: number, month: number) {
        const firstDow = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const cells: JSX.Element[] = [];

        for (let i = 0; i < firstDow; i++) {
            cells.push(<span key={`pad-${i}`} className="drp-day drp-pad" />);
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const date = sod(new Date(year, month, d));
            const rStart  = isRangeStart(date);
            const rEnd    = isRangeEnd(date);
            const ranged  = inRange(date);
            const dis     = disabled(date);
            const isToday = sameDay(date, today);
            const selected = rStart || rEnd;

            // Range band logic for rounded caps
            const hasEnd    = !!(phase === "end" && hovered ? hovered : end);
            const bandLeft  = rStart && hasEnd && (start! < (phase === "end" && hovered ? hovered! : end!));
            const bandRight = rEnd   && start  && (start! < (phase === "end" && hovered ? hovered! : end!));

            cells.push(
                <span
                    key={d}
                    className={[
                        "drp-day",
                        selected  ? "drp-selected" : "",
                        ranged    ? "drp-in-range" : "",
                        rStart    ? "drp-range-start" : "",
                        rEnd      ? "drp-range-end" : "",
                        bandLeft  ? "drp-band-left" : "",
                        bandRight ? "drp-band-right" : "",
                        dis       ? "drp-disabled" : "",
                        isToday   ? "drp-today" : "",
                    ].filter(Boolean).join(" ")}
                    onMouseEnter={() => { if (!dis && phase === "end") setHovered(date); }}
                    onClick={() => { if (!dis) handleClick(date); }}
                >
                    <span className="drp-day-inner">{d}</span>
                </span>
            );
        }

        return (
            <div className="drp-month" key={`${year}-${month}`}>
                <p className="drp-month-label">{MONTH_NAMES[month]} {year}</p>
                <div className="drp-grid">
                    {DAY_LABELS.map((l, i) => (
                        <span key={i} className="drp-dow">{l}</span>
                    ))}
                    {cells}
                </div>
            </div>
        );
    }

    const effectiveEnd = phase === "end" && hovered ? hovered : end;

    return (
        <div className="drp-root" onMouseLeave={() => setHovered(null)}>
            {/* Selected range summary strip */}
            <div className="drp-summary">
                <div className={`drp-summary-chip ${phase === "start" ? "drp-chip-active" : ""}`}
                     onClick={() => setPhase("start")}>
                    <span className="drp-chip-label">Start</span>
                    <span className="drp-chip-date">
                        {start ? start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </span>
                </div>
                <span className="drp-summary-arrow">→</span>
                <div className={`drp-summary-chip ${phase === "end" ? "drp-chip-active" : ""}`}
                     onClick={() => start && setPhase("end")}>
                    <span className="drp-chip-label">End</span>
                    <span className="drp-chip-date">
                        {effectiveEnd ? effectiveEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </span>
                </div>
            </div>

            {/* Calendar */}
            <div className="drp-calendar">
                <button className="drp-nav drp-nav-prev" onClick={prev} type="button">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                <div className="drp-months">
                    {renderMonth(viewYear, viewMonth)}
                    {renderMonth(rightYear, rightMonth)}
                </div>

                <button className="drp-nav drp-nav-next" onClick={next} type="button">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    );
}